import React from "react";
import AllTaskReview from "view/components/AllTaskReview";
import AnonymousRoute from "view/components/routing/AnonymousRoute";
import PrivateRoute from "view/components/routing/PrivateRoute";
import EmailTaskReview from "view/EmailTaskReview";
import EmailTemplatePage from "view/EmailTemplatePage";
import GridPreview from "view/components/GridPreview";
import PaymentReminderPreviewPage from "view/PaymentReminderPreviewPage";
import GlossaryPage from "view/GlossaryPage";
import Login from "view/Login";
import ForgotPassword from "view/Login/ForgotPassword";
import LoginSSO from "view/LoginSSO";
import LoginOnce from "view/LoginOnce";
import SessionChanged from "view/SessionChanged";
import {
  ALL_TASK_REVIEW_PATH,
  ADMIN_LOGIN_PATH,
  ANONYMOUS_SHOW_INFO_PAGE_PATH,
  CHANGE_EMAIL,
  EMAIL_TASK_REVIEW_PATH,
  LEGACY_EMAIL_TASK_REVIEW_PATH,
  EMAIL_TEMPLATE_PREVIEW_PATH,
  PAYMENT_REMINDER_PREVIEW_PATH,
  EMBEDDED_SHOWS_PATH,
  FORGOT_PASSWORD,
  LOGIN_ONCE_PATH,
  LOGIN_PATH,
  LOGIN_SSO_PATH,
  PREVIEW_TASK_GRID_PATH,
  RECOVERY_CODE_CONFIRMATION_PATH,
  RESET_PASSWORD,
  SESSION_CHANGED_PATH,
  SHOW_EXHIBITOR_QUICK_CART_PATH,
  SIGNUP_DEPRECATED_EXHIBITOR_PATH,
  SIGNUP_DEPRECATED_UID_PATH,
  SIGNUP_EXHIBITOR_PATH,
  SIGNUP_PATH,
} from "view/routes";
import {Navigate, RouteProps} from "react-router-dom";
import EmbeddedShows from "view/EmbeddedShows";
import MaintenanceModeGate from "view/components/MaintenanceModeGate";
import SignUpPage from "view/SignUp";
import SignUpExhibitor from "view/SignUp/SignUpExhibitor";
import PublicRoute from "view/components/routing/PublicRoute";
import MaintenanceRoute from "view/components/routing/MaintenanceRoute";
import QuickCart from "view/components/QuickCart";
import SignUpDeprecatedExhibitor from "view/SignUp/SignUpDeprecatedExhibitor";
import SignUpLegacyUidRouter from "view/SignUp/SignUpLegacyUidRouter";
import PasswordReset from "view/PasswordReset";
import RecoveryCodeConfirmation from "view/RecoveryCodeConfirmation";
import ChangeEmail from "view/components/ChangeEmail";

// Everything an admin needs to sign in, so the platform stays reachable for
// them while maintenance mode is on.
const signInRoutes: RouteProps[] = [
  {
    // The separate Admin login page is gone — every role signs in from /login
    // with the same Google button. Kept as a redirect so old links and
    // bookmarks still land somewhere useful.
    path: ADMIN_LOGIN_PATH,
    element: <Navigate to={LOGIN_PATH} replace />,
  },
  {
    path: LOGIN_ONCE_PATH,
    element: <LoginOnce />,
  },
  {
    path: SESSION_CHANGED_PATH,
    element: <SessionChanged />,
  },
  {
    path: LOGIN_PATH,
    element: (
      <AnonymousRoute>
        <Login />
      </AnonymousRoute>
    ),
  },
  {
    path: FORGOT_PASSWORD,
    element: <ForgotPassword />,
  },
  {
    path: RESET_PASSWORD,
    element: <PasswordReset />,
  },
  {
    path: RECOVERY_CODE_CONFIRMATION_PATH,
    element: <RecoveryCodeConfirmation />,
  },
];

const publicRoutes: RouteProps[] = [
  {
    path: LOGIN_SSO_PATH,
    element: <LoginSSO />,
  },
  {
    path: SIGNUP_EXHIBITOR_PATH,
    element: <SignUpExhibitor />,
  },
  {
    path: SIGNUP_PATH,
    element: <SignUpPage />,
  },
  {
    path: SIGNUP_DEPRECATED_UID_PATH,
    element: <SignUpLegacyUidRouter />,
  },
  {
    path: SIGNUP_DEPRECATED_EXHIBITOR_PATH,
    element: <SignUpDeprecatedExhibitor />,
  },
  {
    path: SHOW_EXHIBITOR_QUICK_CART_PATH,
    element: (
      <PublicRoute>
        <QuickCart />
      </PublicRoute>
    ),
  },
  {
    path: CHANGE_EMAIL,
    element: <ChangeEmail />,
  },
  {
    path: EMAIL_TASK_REVIEW_PATH,
    element: <EmailTaskReview />,
  },
  {
    // Legacy alias — old reviewer emails point here; the page detects the
    // stale/invalid key and offers a fresh link via the resend flow.
    path: LEGACY_EMAIL_TASK_REVIEW_PATH,
    element: <EmailTaskReview />,
  },
  {
    path: ALL_TASK_REVIEW_PATH,
    element: <AllTaskReview />,
  },
  {
    path: ANONYMOUS_SHOW_INFO_PAGE_PATH,
    element: <GlossaryPage />,
  },
  {
    path: EMAIL_TEMPLATE_PREVIEW_PATH,
    element: <EmailTemplatePage />,
  },
  {
    path: PAYMENT_REMINDER_PREVIEW_PATH,
    element: <PaymentReminderPreviewPage />,
  },
  {
    path: EMBEDDED_SHOWS_PATH,
    element: (
      <MaintenanceModeGate>
        <EmbeddedShows />
      </MaintenanceModeGate>
    ),
  },
  {
    // Standalone full-window grid preview. Mounted OUTSIDE the app `<Layout>`
    // shell on purpose: no sidebar / top bar / app-bar spacer, so it gets a
    // single page scroll. Bare route (NO `PrivateRoute`) because it doubles as
    // the anonymous target of the `?responseId=<uuid>` link baked into
    // certificate / task-print PDFs — a logged-out viewer must reach it. In-app
    // ("Open Grid") use still works: `GridPreview` reads the postMessage
    // payload and, when logged in, the session show via `useCurrentShow`.
    path: PREVIEW_TASK_GRID_PATH,
    element: <GridPreview />,
  },
];

const commonRoutes: RouteProps[] = [
  ...signInRoutes,
  ...publicRoutes.map((route) => ({
    ...route,
    element: <MaintenanceRoute>{route.element}</MaintenanceRoute>,
  })),
];
export default commonRoutes;
